import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";

import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { accessApi } from "./api";
import { CODE_LENGTH, isCodeComplete, normalizeCode } from "./codes";
import { readFailure } from "./failure";
import { PublicHeading } from "./public-layout";
import { landingFor } from "./routing";

/**
 * The one login mask ([F-029], [F-031], [AC-051], [AC-055]): mail address and
 * password. The system account's address continues with the eight-digit code
 * from its mailbox; everybody else is in after the password. A wrong address,
 * a wrong password and an account not yet confirmed get the same sentence -
 * the server's, shown as it is.
 *
 * Afterwards the person lands where the login interrupted them, or on the
 * first screen ([F-020]); the system account on the administration.
 */
export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const { status, user, reason, refresh } = useAuth();
  const { weiter } = useSearch({ from: "/anmelden" });
  const navigate = useNavigate();
  const [step, setStep] = useState<"password" | "code">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Signed in (now, or already when the page opened): on to the work.
  useEffect(() => {
    if (status === "authenticated") {
      void navigate({ href: weiter ?? landingFor(user?.isSystem === true), replace: true });
    }
  }, [status, user, weiter, navigate]);

  const enter = async (): Promise<void> => {
    const signedIn = await refresh();
    if (!signedIn) setError(t("login.noSession"));
  };

  const submitPassword = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const reply = await accessApi.login(email.trim(), password);
      if (reply.next === "code") {
        setStep("code");
        setPassword("");
        return;
      }
      await enter();
    } catch (failure) {
      setError(readFailure(failure, t("login.failed")).message);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!isCodeComplete(code)) return;
    setBusy(true);
    setError(null);
    try {
      await accessApi.loginCode(normalizeCode(code));
      await enter();
    } catch (failure) {
      setError(readFailure(failure, t("login.failed")).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="login_page">
      <PublicHeading
        testid="login_title"
        title={t("app.title")}
        lead={step === "password" ? t("auth.subtitle") : t("login.codeLead")}
      />

      {reason !== "none" && step === "password" ? (
        <p data-testid="login_notice" role="status" className="mb-4 rounded-md bg-muted p-3 text-sm">
          {reason === "ended" ? t("login.sessionEnded") : t("login.loggedOut")}
        </p>
      ) : null}

      {step === "password" ? (
        <form key="password" data-testid="login_form" className="space-y-4" onSubmit={(event) => void submitPassword(event)}>
          <div className="space-y-2">
            <Label htmlFor="login-email">{t("login.email")}</Label>
            <Input
              id="login-email"
              data-testid="login_email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-password">{t("login.password")}</Label>
            <Input
              id="login-password"
              data-testid="login_password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <Button data-testid="login_submit" type="submit" className="min-h-11 w-full" disabled={busy}>
            {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {t("auth.signIn")}
          </Button>
        </form>
      ) : (
        <form key="code" data-testid="login_code-form" className="space-y-4" onSubmit={(event) => void submitCode(event)}>
          <div className="space-y-2">
            <Label htmlFor="login-code">{t("login.code")}</Label>
            <Input
              id="login-code"
              data-testid="login_code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              maxLength={CODE_LENGTH + 2}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </div>
          <Button data-testid="login_code-submit" type="submit" className="min-h-11 w-full" disabled={busy || !isCodeComplete(code)}>
            {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {t("login.codeSubmit")}
          </Button>
          <Button
            data-testid="login_code-back"
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => {
              setStep("password");
              setCode("");
              setError(null);
            }}
          >
            {t("login.back")}
          </Button>
        </form>
      )}

      {error ? (
        <p data-testid="login_error" role="alert" className="mt-4 text-sm text-destructive-text">
          {error}
        </p>
      ) : null}

      {step === "password" ? (
        <Link to="/passwort-vergessen" data-testid="login_forgotten" className="mt-6 block text-sm text-muted-foreground hover:text-foreground">
          {t("login.forgotten")}
        </Link>
      ) : null}
    </section>
  );
}
